import { useEffect, useState, useRef } from 'react'
import { observer } from 'mobx-react'
import { useParams, useSearchParams } from 'react-router'
import { useNavigate } from 'react-router'

// evertel
import {
    EmailVerificationController,
    BC_CHANNEL_NAME,
    BC_MESSAGES,
    BCMessageType
} from '@evertel/shared/feature-authentication-auth0'
import { useService } from '@evertel/di'
import { Analytics } from '@evertel/analytics'
import { Card, CardBody, Col, Button, Icon, Row, useUI, Spinner, Logo } from '@evertel/web/ui'
import { LoginService } from '@evertel/session/LoginService'


type WaitingPageType = 'registration' | 'email-change' | 'none' | 'checking'

const EmailVerification = observer(() => {

    const analytics = useService(Analytics, [])
    const loginService = useService(LoginService, [])
    const emailVerificationController = useService(EmailVerificationController, [])

    const { showAppLoader, addToast } = useUI()

    const navigate = useNavigate()
    const { token } = useParams<{ token?: string }>()
    const [searchParams] = useSearchParams()

    const [isResending, setIsResending] = useState(false)
    const [verificationComplete, setVerificationComplete] = useState(false)
    const [waitingPageType, setWaitingPageType] = useState<WaitingPageType>('checking')

    const bc = useRef<BroadcastChannel | null>(null)

    const { email: tempEmail, password } = loginService.getTempCredentials()
    const paramEmail = searchParams.get('email')
    const email = tempEmail || paramEmail

    // Determine if this is the resolve page (came from email link)
    const isResolvePage = token === 'auth0'

    // Parse query string for Auth0 redirect params
    const isSuccess = searchParams.get('success') === 'true'
    const isError = searchParams.get('success') === 'false'
    const errorCode = searchParams.get('code')
    const errorMessage = searchParams.get('message')

    useEffect(() => {
        analytics.logView('/email-verification')
        showAppLoader({ isOpen: false, type: 'default' })
    }, [])

    useEffect(() => {
        bc.current = new BroadcastChannel(BC_CHANNEL_NAME)

        if (isResolvePage) {
            // RESOLVE PAGE: Ask if any waiting page exists
            setupResolvePageListeners()
            bc.current.postMessage(BC_MESSAGES.PING)

            // Set timeout for no response
            setTimeout(() => {
                setWaitingPageType(prev => prev === 'checking' ? 'none' : prev)
            }, 1000)
        } else {
            // WAITING PAGE: Listen for pings and respond
            setupWaitingPageListeners()

            // Store email in localStorage for resolve page fallback
            if (email && !localStorage.getItem('email')) {
                localStorage.setItem('email', email)
            }

            // Initialize controller and start polling
            if (email) emailVerificationController.init(email)
        }

        return () => {
            bc.current?.close()
            emailVerificationController.stopGetIsVerifiedPoll()
        }
    }, [email, isResolvePage])

    const setupResolvePageListeners = () => {
        if (!bc.current) return

        bc.current.onmessage = (event) => {
            const message = event.data as BCMessageType

            if (message === BC_MESSAGES.PONG_REGISTRATION) {
                setWaitingPageType('registration')
            } else if (message === BC_MESSAGES.PONG_EMAIL_CHANGE) {
                setWaitingPageType('email-change')
            }
        }
    }

    const setupWaitingPageListeners = () => {
        if (!bc.current) return

        bc.current.onmessage = (event) => {
            const message = event.data as BCMessageType

            if (message === BC_MESSAGES.PING) {
                // Respond to identify ourselves as the email-change waiting page
                bc.current?.postMessage(BC_MESSAGES.PONG_EMAIL_CHANGE)
            }
        }
    }

    useEffect(() => {
        // Show any error messages from querystring
        if (isError && errorCode !== 'already-verified') {
            addToast({
                title: 'Error',
                color: 'danger',
                message: errorMessage || 'An error occurred'
            })
        }
    }, [isError, errorCode, errorMessage])

    useEffect(() => {
        if (emailVerificationController.isVerified ||
            errorCode === 'already-verified' ||
            errorCode === 'success') {
            setVerificationComplete(true)

            // Remove email from localStorage
            if (localStorage.getItem('email')) {
                localStorage.removeItem('email')
            }
        }
    }, [emailVerificationController.isVerified, errorCode])

    const login = async () => {
        try {
            const result = await loginService.loginWithPassword(email, password)

            loginService.clearTempCredentials()

            if ('mfaRequired' in result && result.mfaRequired) {
                navigate('/two-factor')
                return true
            }

            navigate('/landing')
            return true

        } catch (error: any) {
            addToast({
                title: 'Error',
                color: 'danger',
                message: error.message || String(error)
            })
            goToLogin()
            return false
        }
    }

    const resendVerificationEmail = async () => {
        setIsResending(true)

        try {
            await emailVerificationController.resendVerificationEmail()
            addToast({
                color: 'success',
                message: 'A new verification email has been sent'
            })
        } catch (error: any) {
            addToast({
                title: 'Error',
                color: 'danger',
                message: error.message || String(error)
            })
        } finally {
            setIsResending(false)
        }
    }

    const goToLogin = () => {
        const storedEmail = localStorage.getItem('email')
        if (storedEmail) {
            navigate(`/login?email=${encodeURIComponent(storedEmail)}`)
            localStorage.removeItem('email')
        } else {
            navigate('/login')
        }
    }

    // RESOLVE PAGE RENDERING
    if (isResolvePage) {
        return (
            <div className="app d-flex justify-content-center flex-row align-items-center fadeIn animated auth-layout" style={{ height: '100vh' }}>
                <Row className="justify-content-center">
                    <Col md="8">
                        <Card className="mx-4">
                            <CardBody className="p-4">
                                <ResolvePageContent
                                    waitingPageType={waitingPageType}
                                    isSuccess={isSuccess}
                                    isError={isError}
                                    onGoToLogin={goToLogin}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        )
    }

    // WAITING PAGE RENDERING
    return (
        <div className="app d-flex justify-content-center flex-row align-items-center fadeIn animated auth-layout" style={{ height: '100vh' }}>
            <Row className="justify-content-center">
                <Col md="8">
                    <Card className="mx-4">
                        <CardBody className="p-4">
                            {!verificationComplete ? (
                                <WaitingPageContent
                                    email={email}
                                    isResending={isResending}
                                    onResendEmail={resendVerificationEmail}
                                    onGoToLogin={goToLogin}
                                />
                            ) : (
                                <VerificationCompleteView
                                    onContinue={login}
                                    requireLogin={!email || !password}
                                    onGoToLogin={goToLogin}
                                />
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    )
})

// ============================================================================
// RESOLVE PAGE COMPONENTS (shown in the new tab from email link)
// ============================================================================

interface ResolvePageContentProps {
    waitingPageType: WaitingPageType
    isSuccess: boolean
    isError: boolean
    onGoToLogin: () => void
}

const ResolvePageContent: React.FC<ResolvePageContentProps> = ({
    waitingPageType,
    isSuccess,
    isError,
    onGoToLogin
}) => {
    if (waitingPageType === 'checking') {
        return (
            <div className="text-center py-5" style={{minWidth: 400}}>
                <Spinner className="mb-3" />
                <p className="h5">Verifying...</p>
            </div>
        )
    }

    // Error case
    if (isError) {
        return (
            <ResolveErrorView
                waitingPageType={waitingPageType}
                onGoToLogin={onGoToLogin}
            />
        )
    }

    // Success case
    if (isSuccess) {
        return (
            <ResolveSuccessView
                waitingPageType={waitingPageType}
                onGoToLogin={onGoToLogin}
            />
        )
    }

    // Ambiguous case (no success/error params)
    return (
        <div className="text-center py-4">
            <Icon
                name="question-circle"
                color="muted"
                style={{ fontSize: 60, marginBottom: 25 }}
            />
            <h1 className="mb-4">Verification Status Unknown</h1>
            <p className="h5 mb-4">Please try logging in to check your verification status.</p>
            <Button
                color="primary"
                onClick={onGoToLogin}
            >
                Go to Login
            </Button>
        </div>
    )
}

interface ResolveSuccessViewProps {
    waitingPageType: WaitingPageType
    onGoToLogin: () => void
}

const ResolveSuccessView: React.FC<ResolveSuccessViewProps> = ({
    waitingPageType,
    onGoToLogin
}) => {
    const config = {
        registration: {
            title: 'Your email has been verified!',
            message:
                'Please return to the registration tab to continue the registration process.'
        },
        'email-change': {
            title: 'Your new email has been verified!',
            message:
                'Please return to the previous tab to complete login.'
        },
        default: {
            title: 'Your email has been verified!',
            button: {
                label: 'Click here to log in',
                onClick: onGoToLogin
            }
        }
    } as const

    const view = config[waitingPageType] ?? config.default

    return (
        <div className="text-center py-4">
            <Icon
                name="check-circle"
                color="success"
                style={{ fontSize: 60, marginBottom: 25 }}
            />

            <h1 className="mb-4">{view.title}</h1>

            {view.message && (
                <p className="h5 mb-4">
                    {view.message}
                </p>
            )}

            {view.button ? (
                <Button
                    color="primary"
                    onClick={view.button.onClick}
                >
                    {view.button.label}
                </Button>
            ) : (
                <small>You may close this window.</small>
            )}
        </div>
    )
}

interface ResolveErrorViewProps {
    waitingPageType: WaitingPageType
    onGoToLogin: () => void
}

const ResolveErrorView: React.FC<ResolveErrorViewProps> = ({
    waitingPageType,
    onGoToLogin
}) => {
    const hasWaitingPage = waitingPageType === 'registration' || waitingPageType === 'email-change'
    const returnText = waitingPageType === 'registration'
        ? 'Please return to the registration tab to continue.'
        : 'Please return to the previous tab to continue.'

    return (
        <div className="text-center py-4">
            <Icon
                name="exclamation-triangle"
                color="warning"
                style={{ fontSize: 60, marginBottom: 25 }}
            />
            <h1 className="mb-4">Verification Link Issue</h1>
            <p className="h5 mb-4">
                Your email may have already been verified with that link.
            </p>
            {hasWaitingPage ? (
                <>
                    <p className="mb-4">{returnText}</p>
                    <small>You may close this window.</small>
                </>
            ) : (
                <Button
                    color="primary"
                    onClick={onGoToLogin}
                >
                    Go to Login
                </Button>
            )}
        </div>
    )
}

// ============================================================================
// WAITING PAGE COMPONENTS (shown in the original tab)
// ============================================================================

interface WaitingPageContentProps {
    email: string
    isResending: boolean
    onResendEmail: () => void
    onGoToLogin: () => void
}

const WaitingPageContent: React.FC<WaitingPageContentProps> = ({
    email,
    isResending,
    onResendEmail,
    onGoToLogin
}) => {
    return (
        <div className="pb-3">
            <div className="text-center mb-4">
                <Logo />
            </div>
            <h1 className="mb-4 text-center">Please verify your email address</h1>
            <p className="h6">Check your email and click on the verification link to continue.</p>

            <div className="d-flex justify-content-center flex-column">
                <div className="email-box ps-2 pe-2 mt-2 mb-2">
                    <div className="h6 text-center mt-4 mb-3">
                        <p className="mb-2">We sent a verification email to:</p>
                        <span className="fw-bold">
                            {email}
                        </span>
                    </div>
                </div>

                <Button
                    color="primary"
                    type="submit"
                    outline
                    loading={isResending}
                    disabled={isResending}
                    className="mt-2 text-center"
                    onClick={onResendEmail}
                >
                    Resend Verification Email
                </Button>
                <Button
                    color="link"
                    block
                    className="mt-2"
                    onClick={onGoToLogin}
                >
                    Logout
                </Button>
            </div>

            <div className="mt-4">
                <small>
                    <strong>Didn't receive an email?</strong>
                    {' make sure your email is correct above (no typos).'}
                    <br />
                    If it is, check your SPAM folder or try adding no-reply@getevertel.com to your contacts or Safe Senders list.
                </small>
            </div>
        </div>
    )
}

interface VerificationCompleteViewProps {
    onContinue: () => Promise<boolean>
    requireLogin: boolean
    onGoToLogin: () => void
}

const VerificationCompleteView: React.FC<VerificationCompleteViewProps> = ({
    onContinue,
    requireLogin,
    onGoToLogin
}) => {
    const [isLoggingIn, setIsLoggingIn] = useState(false)

    const handleContinue = async () => {
        if (requireLogin) {
            onGoToLogin()
        } else {
            setIsLoggingIn(true)
            await onContinue()
            setIsLoggingIn(false)
        }
    }

    return (
        <div className="py-4 px-5 mx-5">
            <Row className="mb-5">
                <Col className="text-center">
                    <Icon
                        name="check-circle"
                        color="success"
                        style={{ fontSize: 60, marginBottom: 25 }}
                    />
                    <h1 className="mb-4">Your email has been verified!</h1>
                    <p className="h3">
                        {requireLogin
                            ? 'Please login to continue'
                            : 'Please continue to complete your login'
                        }
                    </p>
                    <Button
                        color="primary"
                        type="submit"
                        loading={isLoggingIn}
                        disabled={isLoggingIn}
                        onClick={handleContinue}
                        className="mt-5"
                    >
                        {requireLogin ? 'Login to Continue' : 'Continue'}
                    </Button>
                </Col>
            </Row>
        </div>
    )
}

export { EmailVerification }
